import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE_URL } from '../../../config/api';

/**
 * Scoring Console — READ ONLY.
 *
 * Shows how flags are performing across the corpus so their signal value can be
 * judged: how often each fires, and how the high/legit rates where it fired
 * compare to the base rates. A flag whose "high% where fired" sits near the base
 * rate carries no signal.
 *
 * Editing was deliberately removed. Severity overrides, threshold knobs, config
 * versioning, dry-runs, activation and corpus rescores are all driven by the
 * re-rank script instead, so there is one path for changing ratings rather than
 * a UI and a script that can disagree. The backend write endpoints still exist
 * (POST /bff/admin/classifier and friends) — nothing here calls them.
 */

const LEVELS = ['high', 'elevated', 'low', 'legit'];

const num = (n) => (n == null ? '—' : Number(n).toLocaleString());
const timeAgo = (iso) => {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

function DistDelta({ result }) {
  if (!result?.before || !result?.after) return null;
  return (
    <div className="text-xs text-gray-700 space-y-0.5">
      {LEVELS.map((lvl) => {
        const b = result.before[lvl]; const a = result.after[lvl];
        if (b == null && a == null) return null;
        const up = (a ?? 0) > (b ?? 0);
        return (
          <div key={lvl}>
            <span className="inline-block w-16 capitalize text-gray-500">{lvl}</span>
            <span className="tabular-nums">{num(b)} → <b className={up ? 'text-red-700' : 'text-green-700'}>{num(a)}</b></span>
          </div>
        );
      })}
      {result.flips != null && <div className="text-gray-500">{num(result.flips)} labels change{result.approximate ? ' · ' : ''}{result.approximate && <span className="text-amber-600 font-medium">approximate (pending flag-logic changes not fully simulated)</span>}</div>}
    </div>
  );
}

const SEVERITY_TONE = {
  high: 'text-red-700 font-medium',
  elevated: 'text-orange-600',
  low: 'text-gray-500',
  // Model 3.0: evaluated and shown in reports, never scored.
  informational: 'text-gray-400 italic',
};

export default function ScoringConsole() {
  const [perf, setPerf] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [roster, setRoster] = useState(null);
  const [openFlag, setOpenFlag] = useState(null);   // expanded flag explainer row
  const [briefingOpen, setBriefingOpen] = useState(false);
  // Flag table sort: click a header to sort, click again to flip direction.
  const [sort, setSort] = useState({ key: 'fires', dir: 'desc' });
  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }));

  const load = useCallback(() => {
    fetch(`${API_BASE_URL}/bff/admin/flag-performance`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null)).then(setPerf).catch(() => {});
    fetch(`${API_BASE_URL}/bff/admin/classifier`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null)).then(setCfg).catch(() => {});
    fetch(`${API_BASE_URL}/bff/admin/score-model`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null)).then(setRoster).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const versions = cfg?.versions || [];
  // Severity shown per flag: an active config may override the scanner default.
  const activeSeverities = versions.find((v) => v.version === cfg?.active)?.config?.flag_severities || {};
  // Scanner-default severity + the plain-English explainer, keyed by flag name.
  const flagMeta = useMemo(() => {
    const m = {};
    for (const f of roster?.flags || []) m[f.name] = f;
    return m;
  }, [roster]);

  return (
    <div className="border border-gray-200 rounded-xl p-5 mb-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-lg font-semibold text-black">Scoring console</h2>
        <button onClick={load} className="text-xs text-blue-600 hover:underline">Refresh</button>
      </div>
      <p className="text-xs text-gray-400 mb-4">
        Read-only view of flag performance and the score model. Ratings are changed with the re-rank
        script, not from here.
      </p>

      {/* Flag performance */}
      {!perf ? <p className="text-gray-400 text-sm">Loading flag performance…</p> : (
        <>
          <p className="text-xs text-gray-500 mb-2">
            Base rates across {num(perf.base.total)} domains: high {perf.base.highPct}% · elevated {perf.base.elevatedPct}% · legit {perf.base.legitPct}%.
            A flag whose &ldquo;high% where fired&rdquo; ≈ base rate carries no signal. Showing the {perf.flags.length} flags observed firing.
            {flagMeta && Object.keys(flagMeta).length > 0
              ? <> Click any flag for what it means and why it fires. <span className="italic text-gray-400">informational</span> flags are shown in reports but never affect the rating.</>
              : <> Severity and explanations load from the vendored flag taxonomy.</>}
          </p>
          <div className="overflow-y-auto max-h-96 border border-gray-100 rounded-lg mb-3">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50">
                <tr className="text-left text-gray-400">
                  {[
                    ['flag', 'Flag', ''],
                    ['fires', 'Fires', 'text-right'],
                    ['fireRatePct', 'Fire%', 'text-right'],
                    ['highWhereFiredPct', 'High% fired', 'text-right'],
                    ['legitWhereFiredPct', 'Legit% fired', 'text-right'],
                    ['severity', 'Severity', ''],
                  ].map(([key, label, align]) => (
                    <th
                      key={key}
                      className={`py-1.5 px-2 ${align} cursor-pointer select-none hover:text-gray-700`}
                      onClick={() => toggleSort(key)}
                      title="Click to sort"
                    >
                      {label}
                      {sort.key === key && <span className="ml-1 text-gray-600">{sort.dir === 'desc' ? '▼' : '▲'}</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[...perf.flags].sort((a, b) => {
                  const rank = { high: 3, elevated: 2, low: 1, informational: 0 };
                  const sev = (f) => rank[activeSeverities[f.flag] || flagMeta[f.flag]?.severity] ?? -1;
                  const va = sort.key === 'severity' ? sev(a) : sort.key === 'flag' ? a.flag : Number(a[sort.key]) || 0;
                  const vb = sort.key === 'severity' ? sev(b) : sort.key === 'flag' ? b.flag : Number(b[sort.key]) || 0;
                  const cmp = typeof va === 'string' ? va.localeCompare(vb) : va - vb;
                  return sort.dir === 'desc' ? -cmp : cmp;
                }).map((f) => {
                  const override = activeSeverities[f.flag];
                  const meta = flagMeta[f.flag];
                  const severity = override || meta?.severity;
                  const isOpen = openFlag === f.flag;
                  const tone = SEVERITY_TONE[severity] || 'text-gray-400';
                  return (
                    <React.Fragment key={f.flag}>
                      <tr
                        className={`border-t border-gray-50 ${meta ? 'cursor-pointer hover:bg-gray-50' : ''} ${isOpen ? 'bg-gray-50' : ''}`}
                        onClick={() => meta && setOpenFlag(isOpen ? null : f.flag)}
                        title={meta ? 'Click for what this flag means' : undefined}
                      >
                        <td className="py-1 px-2 font-mono text-xs text-black">
                          {meta && <span className="inline-block w-3 text-gray-400">{isOpen ? '▾' : '▸'}</span>}
                          {f.flag}
                        </td>
                        <td className="py-1 px-2 text-right tabular-nums text-gray-600">{num(f.fires)}</td>
                        <td className="py-1 px-2 text-right tabular-nums text-gray-600">{f.fireRatePct}%</td>
                        <td className={`py-1 px-2 text-right tabular-nums ${f.highWhereFiredPct > perf.base.highPct * 1.5 ? 'text-red-700 font-medium' : 'text-gray-500'}`}>{f.highWhereFiredPct}%</td>
                        <td className="py-1 px-2 text-right tabular-nums text-gray-500">{f.legitWhereFiredPct}%</td>
                        <td className={`py-1 px-2 text-xs capitalize ${tone}`}>
                          {severity || 'unknown'}
                          {override && <span className="ml-1 text-blue-700" title="overridden by the active config">*</span>}
                        </td>
                      </tr>
                      {isOpen && meta && (
                        <tr className="bg-gray-50 border-t border-gray-100">
                          <td colSpan={6} className="px-2 pb-3 pt-1">
                            <div className="text-sm text-gray-600 max-w-3xl space-y-2">
                              <p className="text-gray-900 font-medium">{meta.summary}</p>
                              {meta.means && (
                                <div>
                                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">What this means</p>
                                  <p className="mt-0.5">{meta.means}</p>
                                </div>
                              )}
                              {meta.matters && (
                                <div>
                                  <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Why it matters</p>
                                  <p className="mt-0.5">{meta.matters}</p>
                                </div>
                              )}
                              <p className="text-xs text-gray-400 pt-1">
                                Fires when: {meta.fires || meta.detail}
                                {' · '}collector <span className="font-mono">{meta.collector}</span>
                                {' · '}scanner default severity <span className="capitalize">{meta.severity}</span>
                                {override && <> · overridden to <span className="capitalize text-blue-700">{override}</span> by the active config</>}
                              </p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Score model — weights are scanner-code-owned, rendered from the
          vendored roster so it can never drift from the scorer. */}
      <div className="mt-6">
        <p className="text-xs font-medium text-gray-500 mb-2">Score model (0–100, separate from the risk label)</p>
        {!roster?.score_model?.weights ? (
          <p className="text-sm text-gray-400">
            Ships with the scanner re-tune (canonical <span className="font-mono">flag_taxonomy.json</span>).
            The 0–100 score is a weighted sum of 11 normalized categories; weights are scanner-code-owned and render here once the roster lands.
          </p>
        ) : (
          <div className="space-y-1">
            {Object.entries(roster.score_model.weights)
              .sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))
              .map(([cat, w]) => {
                const maxW = Math.max(...Object.values(roster.score_model.weights).map((x) => Math.abs(x)));
                return (
                  <div key={cat} className="flex items-center gap-2 text-xs">
                    <span className="w-36 flex-shrink-0 text-gray-600 font-mono truncate" title={cat}>{cat}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div className={`h-full rounded-full ${w < 0 ? 'bg-red-500' : 'bg-blue-500'}`} style={{ width: `${Math.max(3, Math.round((Math.abs(w) / maxW) * 100))}%` }} />
                    </div>
                    <span className="w-8 text-right tabular-nums text-gray-500 flex-shrink-0">{w}</span>
                    <span className="hidden md:block flex-1 text-gray-400 truncate">{roster.score_model.descriptions?.[cat] || ''}</span>
                  </div>
                );
              })}
          </div>
        )}
      </div>

      {/* Config version history — informational; new versions come from the script. */}
      <div className="mt-6">
        <p className="text-xs font-medium text-gray-500 mb-2">
          Config versions {cfg?.active != null && <span className="text-green-700">· v{cfg.active} active</span>}
        </p>
        {versions.length === 0 ? (
          <p className="text-sm text-gray-400">None yet — the scanner re-tune ships as v1.</p>
        ) : (
          <div className="space-y-3">
            {versions.map((v) => {
              const isActive = cfg.active === v.version;
              const dryrunDone = v.dryrun_status === 'done' && v.dryrun_result;
              return (
                <div key={v.version} className={`border rounded-lg p-3 ${isActive ? 'border-green-300 bg-green-50/40' : 'border-gray-200'}`}>
                  <div className="text-sm">
                    <b>v{v.version}</b>
                    {isActive && <span className="ml-2 px-1.5 py-0.5 rounded bg-green-100 text-green-700 text-xs font-medium">active</span>}
                    {v.note && <span className="ml-2 text-gray-600">{v.note}</span>}
                    <span className="ml-2 text-xs text-gray-400">{timeAgo(v.created_at)}{v.created_by_email ? ` · ${v.created_by_email}` : ''}</span>
                  </div>
                  <div className="mt-2 text-xs text-gray-500">
                    {v.config?.flag_severities && <span>{Object.keys(v.config.flag_severities).length} severity override(s) </span>}
                    {v.config?.classifier && <span>· classifier knobs set</span>}
                  </div>
                  {dryrunDone && (
                    <div className="mt-2 border-t border-gray-100 pt-2">
                      <p className="text-xs font-medium text-gray-500 mb-1">Dry-run (completed {timeAgo(v.dryrun_completed_at)})</p>
                      <DistDelta result={v.dryrun_result} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* How ranking works — collapsed by default; this is reference material,
          not something you need in view while reading the tables above. */}
      {roster?.model && (
        <div className="mt-6 border-t border-gray-100 pt-4">
          <button
            onClick={() => setBriefingOpen((o) => !o)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-black"
            aria-expanded={briefingOpen}
          >
            <span className="text-gray-400">{briefingOpen ? '▾' : '▸'}</span>
            How domains are ranked and scored
            {roster.version && <span className="ml-2 text-xs font-normal text-gray-400">model {roster.version}</span>}
          </button>

          {briefingOpen && (
            <div className="mt-3 text-sm text-gray-600 max-w-4xl space-y-4">
              <p>{roster.model.summary}</p>

              <div>
                <p className="text-xs font-medium text-gray-500 mb-1.5">Risk bands</p>
                <div className="space-y-1">
                  {roster.model.levels.map((l) => (
                    <div key={l.level} className="flex gap-2 text-xs">
                      <span className={`w-16 flex-shrink-0 capitalize font-medium ${SEVERITY_TONE[l.level] || 'text-green-700'}`}>{l.level}</span>
                      <span className="text-gray-600">{l.meaning}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-500 mb-1.5">
                  Rule ladder — evaluated in order, first match wins
                </p>
                <div className="border border-gray-100 rounded-lg overflow-hidden">
                  {roster.model.rules.map((r) => (
                    <div key={r.order} className="flex gap-2 text-xs px-2 py-1.5 border-b border-gray-50 last:border-b-0">
                      <span className="w-5 flex-shrink-0 text-gray-400 tabular-nums">{r.order}</span>
                      <span className="flex-1 text-gray-700">
                        {r.condition}
                        {r.overridable && <span className="ml-1 text-gray-400">(can be overridden by legit signals)</span>}
                      </span>
                      <span className={`w-16 flex-shrink-0 text-right capitalize font-medium ${SEVERITY_TONE[r.result] || 'text-green-700'}`}>
                        → {r.result}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs font-medium text-gray-500 mb-1.5">Worth knowing</p>
                <ul className="list-disc pl-5 space-y-1 text-xs text-gray-600">
                  {roster.model.caveats.map((c, i) => <li key={i}>{c}</li>)}
                </ul>
              </div>

              {Array.isArray(roster.model.changelog) && roster.model.changelog.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1.5">Model history</p>
                  <div className="space-y-1.5">
                    {roster.model.changelog.map((c) => (
                      <div key={c.version} className="flex gap-2 text-xs">
                        <span className="w-24 flex-shrink-0 text-gray-700 font-mono">{c.version}<span className="block text-gray-400 font-sans">{c.date}</span></span>
                        <span className="text-gray-600">{c.summary}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-400">
                {roster.flags.length} flags · {roster.legitSignals.length} legit signals ·
                transcribed from <span className="font-mono">{roster.generatedFrom}</span> (taxonomy v{roster.version}).
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
